import {
  CollapsibleCard,
  ConfirmationDialogBox,
  DatePicker,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableFoot as TableFooter,
  TableHead,
  TableHeaderCell,
  TableRow,
  TextField,
  Typography,
} from '@atgfr8/atg-ui-tailwind';
import { BasicModal, Button } from '@components';
import { EmptyData } from '@components/EmptyData/EmptyData';
import {
  checkIfDialogBoxIsOpen,
  closeDialogBox,
  openDialogBox,
} from '@store/dialogBox';
import { openModal } from '@store/modals';
import { orderStore, updateOrderState } from '@store/orders';
import {
  PlusCircleOutlineIcon,
  CreditCardIcon,
  DeleteIcon,
} from '@atgfr8/icons';
import { PaymentViewModel } from '@typeDefinitions/paymentTypes';
import { formatDataTestId } from '@utils/dataTestIdFormatter';
import { formatAmount, isAdmin } from '@utils/utils';
import { DELETE_CUSTOMER_PAYMENTS_LINE_ITEM_DIALOG_ID } from '@views/order/constants';
import { DateTime } from 'luxon';
import { Accessor, Index, Show, createSignal } from 'solid-js';

import { removeTableRow } from '../carrier/stops/stopsEditor/utilFunctions';
import { PayWithCreditCardModal } from './CustomerInformation/index';
import classes from './classes';

export const CustomerPayments = () => {
  const [deleteRow, setDeleteRow] = createSignal<number | null>(null);

  const addRow = () => {
    if (orderStore.isReadOnly) {
      return;
    }

    const payments = [...orderStore.order.payments];
    payments.push({
      operationType: 'Insert',
      id: 0,
      type: 'Payment',
      description: '',
      amount: 0,
      status: 'PendingSync',
      paidDate: new Date().toISOString(),
      referenceNum: '',
      checkNumber: null,
      // orderId: orderStore.order.id,
    });
    updateOrderState({ payments: payments });
  };

  const headers = ['', 'Date', 'Reference', 'Description', 'Amount'];

  const cellStyles = {
    padding: '1px',
  };

  const computeTotalPayments = () => {
    return orderStore.order.payments.length > 0
      ? orderStore.order.payments
          .filter((v) => v.operationType !== 'Delete')
          .reduce((acc, item) => Number(acc) + Number(item.amount), 0)
      : 0;
  };

  const computeTotalLineItems = () => {
    return orderStore.order.lineItems.length > 0
      ? orderStore.order.lineItems
          .filter((v) => v.operationType !== 'Delete')
          .reduce(
            (acc, item) => acc + Number(item.rate) * Number(item.quantity),
            0,
          )
      : 0;
  };

  const handleCustomerPaymentChange = (
    key: string,
    value: string | number,
    index: number,
  ) => {
    const payments = [...orderStore.order.payments];
    if (payments[index].operationType === 'None') {
      payments[index] = {
        ...payments[index],
        [key]: value,
        operationType: 'Update',
      };
    } else {
      payments[index] = { ...payments[index], [key]: value };
    }
    updateOrderState({
      payments: payments,
    });
  };

  return (
    <CollapsibleCard
      startTitle={orderStore.isMobile ? 'Payments' : 'Customer Payments'}
      class="mb-2"
      loading={orderStore.loading}
      endIcon={
        <Show when={orderStore.order.id}>
          <Button
            label="Pay With Card"
            startIcon={<CreditCardIcon />}
            onClick={() => openModal('payWithCreditCardModal')}
            sx={{
              'background-color': '#468DB5',
              color: '#FFF',
              'line-height': '22px',
              'margin-top': '-1px',
              'margin-bottom': '-1px',
            }}
            disabled={orderStore.isReadOnly}
            size="sm"
            dataTestId={formatDataTestId('Pay With Card', 'button')}
          />
        </Show>
      }
    >
      <div>
        <Table>
          <TableHead>
            <TableRow>
              {headers
                .filter((x) => x !== '' && !isAdmin())
                .map((item) => (
                  <TableHeaderCell style={cellStyles} class="text-xs!">
                    {item}
                  </TableHeaderCell>
                ))}
            </TableRow>
          </TableHead>
          <Show
            when={
              orderStore.order.payments.filter(
                (item) => item.operationType !== 'Delete',
              ).length === 0
            }
          >
            <TableBody class="w-full mobile:h-5 non-mobile:h-64 bg-[#BFDFD8]">
              <TableRow>
                <TableCell colSpan={headers.length} style={{ border: 0 }}>
                  <EmptyData />
                </TableCell>
              </TableRow>
            </TableBody>
          </Show>
          <Show when={orderStore.order.payments.length > 0}>
            <TableBody class={classes.customerPayments.tableBg}>
              <Index each={orderStore.order.payments}>
                {(item: Accessor<PaymentViewModel>, index) => (
                  <Show when={item().operationType !== 'Delete'}>
                    <TableRow class={classes.customerPayments.tableRow}>
                      <Show when={isAdmin()}>
                        <TableCell style={{ padding: '0px' }}>
                          <IconButton
                            dataTestId="delete-customer-payment-button"
                            onClick={() => {
                              setDeleteRow(index);
                              openDialogBox(
                                DELETE_CUSTOMER_PAYMENTS_LINE_ITEM_DIALOG_ID,
                              );
                            }}
                            disabled={orderStore.isReadOnly}
                          >
                            <DeleteIcon
                              class={classes.customerPayments.deleteIcon}
                              size={20}
                            />
                          </IconButton>
                        </TableCell>
                      </Show>
                      <TableCell
                        style={{
                          ...cellStyles,
                        }}
                      >
                        <div class="tablet:max-w-[100px] 1080p:max-w-[500px]">
                          <DatePicker
                            value={
                              DateTime.fromJSDate(
                                new Date(item().paidDate),
                              ).toISO() || ''
                            }
                            handleChange={(value: string) => {
                              handleCustomerPaymentChange(
                                'paidDate',
                                value,
                                index,
                              );
                            }}
                            placeholder="Select Date"
                            disabled={orderStore.isReadOnly || !isAdmin()}
                            dataTestId="paid-date-datePicker"
                          />
                        </div>
                      </TableCell>
                      <TableCell
                        style={{
                          ...cellStyles,
                        }}
                      >
                        <div class="tablet:max-w-[90px] 1080p:max-w-[500px]">
                          <TextField
                            size="md"
                            value={item().referenceNum as string}
                            onChange={(value: string) => {
                              handleCustomerPaymentChange(
                                'referenceNum',
                                value,
                                index,
                              );
                            }}
                            class={'bg-white rounded-lg'}
                            label=""
                            placeholder="Reference #"
                            disabled={orderStore.isReadOnly || !isAdmin()}
                            dataTestId="reference-number-textInput"
                          />
                        </div>
                      </TableCell>
                      <TableCell style={{ ...cellStyles }}>
                        <div class="tablet:max-w-[100px] 1080p:max-w-[500px]">
                          <TextField
                            size="md"
                            value={item().description}
                            onChange={(value: string) => {
                              handleCustomerPaymentChange(
                                'description',
                                value,
                                index,
                              );
                            }}
                            class={'bg-white rounded-lg'}
                            label=""
                            placeholder="Description"
                            disabled={orderStore.isReadOnly || !isAdmin()}
                            dataTestId="description-textInput"
                          />
                        </div>
                      </TableCell>
                      <TableCell style={{ ...cellStyles }}>
                        <div class="tablet:max-w-[60px] 1080p:max-w-[500px]">
                          <TextField
                            size="md"
                            label=""
                            placeholder="Amount"
                            type="number"
                            value={item().amount}
                            onChange={(value: unknown) => {
                              handleCustomerPaymentChange(
                                'amount',
                                value as number,
                                index,
                              );
                            }}
                            dataTestId="amount-textInput"
                            class={'bg-white rounded-lg'}
                            disabled={orderStore.isReadOnly || !isAdmin()}
                          />
                        </div>
                      </TableCell>
                    </TableRow>
                  </Show>
                )}
              </Index>
            </TableBody>
          </Show>
          <TableFooter class={classes.customerPayments.tableFooter}>
            <TableRow class={classes.customerPayments.tableFooterRow}>
              <Show when={isAdmin()}>
                <TableCell style={cellStyles}>
                  <PlusCircleOutlineIcon
                    class={`${classes.customerPayments.addIcon} ${
                      orderStore.isReadOnly ? '' : 'cursor-pointer'
                    }`}
                    onClick={addRow}
                  />
                </TableCell>
              </Show>
              <TableCell></TableCell>
              <TableCell></TableCell>
              <TableCell style={cellStyles}>
                <Typography
                  variant="body1"
                  class={classes.customerPayments.totalLabel}
                >
                  Total
                </Typography>
              </TableCell>
              <TableCell style={cellStyles}>
                <Typography
                  variant="body1"
                  class={classes.customerPayments.totalAmount}
                >
                  {formatAmount(computeTotalPayments())}
                </Typography>
              </TableCell>
            </TableRow>
            <TableRow class={classes.customerPayments.tableFooterRow}>
              <Show when={isAdmin()}>
                <TableCell></TableCell>
              </Show>
              <TableCell></TableCell>
              <TableCell></TableCell>
              <TableCell style={cellStyles}>
                <Typography
                  variant="body1"
                  class={classes.customerPayments.totalLabel}
                >
                  Outstanding
                </Typography>
              </TableCell>
              <TableCell style={cellStyles}>
                <Typography
                  variant="body1"
                  class={classes.customerPayments.totalAmount}
                >
                  {formatAmount(
                    computeTotalLineItems() - computeTotalPayments(),
                  )}
                </Typography>
              </TableCell>
            </TableRow>
          </TableFooter>
        </Table>
      </div>
      <BasicModal
        id="payWithCreditCardModal"
        title="Pay With Credit Card"
        width={orderStore.isMobile ? '95vw' : '900px'}
        footer={false}
        showClose={false}
      >
        <PayWithCreditCardModal />
      </BasicModal>
      <ConfirmationDialogBox
        isDialogOpen={checkIfDialogBoxIsOpen(
          DELETE_CUSTOMER_PAYMENTS_LINE_ITEM_DIALOG_ID,
        )}
        dataTestId={DELETE_CUSTOMER_PAYMENTS_LINE_ITEM_DIALOG_ID}
        id={DELETE_CUSTOMER_PAYMENTS_LINE_ITEM_DIALOG_ID}
        title={'Are you sure you want to delete this payment?'}
        onSubmit={() => {
          removeTableRow(deleteRow()!, orderStore.order, 'order', 'payments');
          setDeleteRow(null);
        }}
        onSubmitText="Delete"
        closeDialogBox={(_reason) => {
          setDeleteRow(null);
          closeDialogBox(DELETE_CUSTOMER_PAYMENTS_LINE_ITEM_DIALOG_ID);
        }}
      />
    </CollapsibleCard>
  );
};
